import React, { useState } from 'react';
import { PageHeader } from '@components/layout/Breadcrumb';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import Input from '@components/forms/Input';
import TextArea from '@components/forms/TextArea';
import Select from '@components/forms/Select';
import Button from '@components/common/Button';
import Alert from '@components/common/Alert';
import LoadingSpinner from '@components/common/LoadingSpinner';
import useForm from '@hooks/useForm';
import { validateContactForm } from '@utils/validators';
import { submitContactForm } from '@services/contactService';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
} from 'react-icons/fi';
import { CONTACT_INFO } from '@utils/constants';

/**
 * Contact Page Component
 * Contact form, map, and contact information
 */
const Contact = () => {
  const breadcrumbs = [{ label: 'Contact Us', path: '/contact' }];
  const [submitStatus, setSubmitStatus] = useState(null);

  // Initial form values
  const initialValues = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  };

  // Form submission handler
  const handleSubmit = async (values) => {
    try {
      setSubmitStatus(null);
      
      // Submit to API
      await submitContactForm(values);
      
      // Show success message
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for contacting us! We will get back to you within 24 hours.',
      });
      
      // Reset form
      resetForm();
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to send message. Please try again or contact us directly.',
      });
    }
  };

  // Use form hook
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit: onSubmit,
    resetForm,
  } = useForm(initialValues, handleSubmit, validateContactForm);

  return (
    <div className="contact-page">
      {/* Page Header */}
      <PageHeader
        title="Contact Us"
        description="Get in touch with our team. We're here to help with your questions and inquiries"
        breadcrumbs={breadcrumbs}
      />

      {/* Contact Form & Info Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form - 2 columns */}
            <div className="lg:col-span-2">
              <SectionHeader
                subtitle="Send us a message"
                title="We'd Love to Hear From You"
                description="Fill out the form below and our team will respond within 24 hours"
                centered={false}
                className="mb-8"
              />

              {/* Status Alert */}
              {submitStatus && (
                <Alert
                  type={submitStatus.type}
                  message={submitStatus.message}
                  dismissible
                  onDismiss={() => setSubmitStatus(null)}
                  className="mb-6"
                />
              )}

              {/* Contact Form */}
              <Card className="bg-bukawa-cream border border-bukawa-gray-200">
                <form onSubmit={onSubmit} noValidate>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Name */}
                    <Input
                      label="Full Name"
                      name="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.name}
                      touched={touched.name}
                      required
                      disabled={isSubmitting}
                    />

                    {/* Email */}
                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.email}
                      touched={touched.email}
                      required
                      disabled={isSubmitting}
                      icon={<FiMail />}
                    />

                    {/* Phone */}
                    <Input
                      label="Phone Number"
                      name="phone"
                      type="tel"
                      placeholder="+256 XXX XXX XXX"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.phone}
                      touched={touched.phone}
                      required
                      disabled={isSubmitting}
                      icon={<FiPhone />}
                      helper="Format: +256XXXXXXXXX or 07XXXXXXXX"
                    />

                    {/* Subject */}
                    <Select
                      label="Subject"
                      name="subject"
                      placeholder="Select a subject"
                      value={values.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.subject}
                      touched={touched.subject}
                      required
                      disabled={isSubmitting}
                      options={[
                        { value: 'general', label: 'General Inquiry' },
                        { value: 'products', label: 'Product Information' },
                        { value: 'bulk-order', label: 'Bulk Order Request' },
                        { value: 'membership', label: 'Membership Inquiry' },
                        { value: 'partnership', label: 'Partnership Opportunity' },
                        { value: 'support', label: 'Customer Support' },
                        { value: 'other', label: 'Other' },
                      ]}
                    />
                  </div>

                  {/* Message */}
                  <TextArea
                    label="Message"
                    name="message"
                    placeholder="Tell us more about your inquiry..."
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.message}
                    touched={touched.message}
                    required
                    disabled={isSubmitting}
                    rows={6}
                    maxLength={1000}
                    showCount
                  />

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      icon={<FiSend />}
                      iconPosition="right"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>

            {/* Contact Information - 1 column */}
            <div className="lg:col-span-1">
              <SectionHeader
                subtitle="Contact Details"
                title="Get In Touch"
                centered={false}
                className="mb-8"
              />

              <div className="space-y-6">
                {/* Office Address */}
                <Card hover className="bg-white border border-bukawa-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-bukawa-primary-50 rounded-lg flex items-center justify-center">
                      <FiMapPin className="h-6 w-6 text-bukawa-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                        Office Address
                      </h4>
                      <p className="text-sm text-bukawa-gray-600 leading-relaxed">
                        {CONTACT_INFO.location}
                      </p>
                      <p className="text-sm text-bukawa-gray-600 mt-2">
                        {CONTACT_INFO.address}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Phone Numbers */}
                <Card hover className="bg-white border border-bukawa-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-bukawa-secondary-50 rounded-lg flex items-center justify-center">
                      <FiPhone className="h-6 w-6 text-bukawa-secondary-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                        Phone Numbers
                      </h4>
                      <a
                        href={`tel:${CONTACT_INFO.phone1}`}
                        className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600 mb-1"
                      >
                        {CONTACT_INFO.phone1}
                      </a>
                      <a
                        href={`tel:${CONTACT_INFO.phone2}`}
                        className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600"
                      >
                        {CONTACT_INFO.phone2}
                      </a>
                    </div>
                  </div>
                </Card>

                {/* Email */}
                <Card hover className="bg-white border border-bukawa-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-bukawa-primary-50 rounded-lg flex items-center justify-center">
                      <FiMail className="h-6 w-6 text-bukawa-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                        Email Address
                      </h4>
                      <a
                        href={`mailto:${CONTACT_INFO.email}`}
                        className="text-sm text-bukawa-primary hover:text-bukawa-primary-600 break-all"
                      >
                        {CONTACT_INFO.email}
                      </a>
                    </div>
                  </div>
                </Card>

                {/* Office Hours */}
                <Card hover className="bg-white border border-bukawa-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-bukawa-secondary-50 rounded-lg flex items-center justify-center">
                      <FiClock className="h-6 w-6 text-bukawa-secondary-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                        Office Hours
                      </h4>
                      <div className="text-sm text-bukawa-gray-600 space-y-1">
                        <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                        <p>Saturday: 8:00 AM - 1:00 PM</p>
                        <p>Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Quick Links */}
              <div className="mt-8">
                <h4 className="font-semibold text-bukawa-gray-900 mb-4">
                  Quick Links
                </h4>
                <div className="space-y-2">
                  <a
                    href="/membership"
                    className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600"
                  >
                    → Become a Member
                  </a>
                  <a
                    href="/products"
                    className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600"
                  >
                    → View Our Products
                  </a>
                  <a
                    href="/services"
                    className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600"
                  >
                    → Our Services
                  </a>
                  <a
                    href="/faq"
                    className="block text-sm text-bukawa-primary hover:text-bukawa-primary-600"
                  >
                    → FAQs
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <LocationMap />

      {/* Alternative Contact Methods */}
      <section className="section bg-bukawa-light">
        <div className="container-custom">
          <SectionHeader
            subtitle="Other Ways to Reach Us"
            title="We're Here to Help"
            className="mb-12"
          />

          <div className="grid md:grid-cols-3 gap-6">
            <Card hover className="text-center bg-white">
              <div className="w-16 h-16 bg-bukawa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <FiPhone className="h-8 w-8" />
              </div>
              <h4 className="text-lg font-semibold text-bukawa-gray-900 mb-2">
                Call Us
              </h4>
              <p className="text-sm text-bukawa-gray-600 mb-4">
                Speak directly with our team during office hours
              </p>
              <a
                href={`tel:${CONTACT_INFO.phone1}`}
                className="btn-primary btn-sm"
              >
                Call Now
              </a>
            </Card>

            <Card hover className="text-center bg-white">
              <div className="w-16 h-16 bg-bukawa-secondary text-bukawa-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMail className="h-8 w-8" />
              </div>
              <h4 className="text-lg font-semibold text-bukawa-gray-900 mb-2">
                Email Us
              </h4>
              <p className="text-sm text-bukawa-gray-600 mb-4">
                Send us an email and we'll respond within 24 hours
              </p>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="btn-secondary btn-sm"
              >
                Send Email
              </a>
            </Card>

            <Card hover className="text-center bg-white">
              <div className="w-16 h-16 bg-bukawa-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMapPin className="h-8 w-8" />
              </div>
              <h4 className="text-lg font-semibold text-bukawa-gray-900 mb-2">
                Visit Us
              </h4>
              <p className="text-sm text-bukawa-gray-600 mb-4">
                Come to our office in Iganga District
              </p>
              <a
                href="#map"
                className="btn-outline btn-sm"
              >
                View Map
              </a>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

/**
 * LocationMap Component
 * Displays location map with coordinates
 */
const LocationMap = () => {
  const { coordinates } = CONTACT_INFO;

  return (
    <section id="map" className="section bg-white">
      <div className="container-custom">
        <SectionHeader
          subtitle="Find Us"
          title="Our Location"
          description="Visit us at our headquarters in Iganga District, Eastern Uganda"
          className="mb-8"
        />

        <div className="rounded-2xl overflow-hidden shadow-large border border-bukawa-gray-200">
          {/* Embedded Map - Using iframe for Google Maps */}
          <div className="relative w-full h-96 bg-bukawa-gray-100">
            <iframe
              title="Bukawa ACE Ltd Location"
              src={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&hl=en&z=14&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Map Info Bar */}
          <div className="bg-white p-6 border-t border-bukawa-gray-200">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-bukawa-gray-900 mb-1">
                  Bukawa Area Marketing Enterprise Cooperative Society Ltd
                </h4>
                <p className="text-sm text-bukawa-gray-600">
                  {CONTACT_INFO.location}
                </p>
              </div>
              <a
                href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-sm whitespace-nowrap"
              >
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;